import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Lock, Loader2, Eye } from "lucide-react";
import { endpoints } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function VodPlayer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isSubscribed } = useAuth();
  const [v, setV] = useState(null);
  const [loading, setLoading] = useState(true);
  const [streamUrl, setStreamUrl] = useState("");

  useEffect(() => {
    endpoints.vodDetail(id).then(async (r) => {
      setV(r.data);
      if (!r.data?.locked) endpoints.vodView(id).catch(() => {});
      // Build a protected stream URL: gated storage videos use a SHORT-LIVED signed token (not the session JWT).
      if (r.data?.source === "storage" && r.data?.streamPath) {
        const base = `${process.env.REACT_APP_BACKEND_URL}${r.data.streamPath}`;
        if (r.data.access && r.data.access !== "free" && !r.data.locked) {
          try {
            const tr = await endpoints.vodPlaybackToken(id);
            setStreamUrl(`${base}?t=${encodeURIComponent(tr.data.token)}`);
          } catch { setStreamUrl(base); }
        } else {
          setStreamUrl(base);
        }
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-[#E11D2A]" /></div>;
  if (!v) return <div className="text-center py-32 text-slate-400">Video not found.</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="vod-player-page">
      <Link to="/videos" className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-4"><ArrowLeft className="w-4 h-4" /> All videos</Link>

      {v.locked ? (
        <div className="aspect-video w-full rounded-2xl bg-[#121620] border border-[#273042] flex flex-col items-center justify-center text-center p-6" data-testid="vod-locked">
          <Lock className="w-10 h-10 text-amber-400 mb-3" />
          <h2 className="text-white font-bold text-xl">{v.access === "premium" ? "Premium-only video" : "Subscriber-only video"}</h2>
          <p className="text-slate-400 text-sm mt-1 mb-4">{v.access === "premium" ? "Upgrade to a Premium membership to watch this video." : "Subscribe to watch this and all premium content on B&\u0026B Kigali FM."}</p>
          <button onClick={() => navigate("/pricing")} data-testid="vod-subscribe-btn" className="h-11 px-6 rounded-full bg-[#E11D2A] text-white font-bold">View plans</button>
        </div>
      ) : v.source === "storage" && v.streamPath ? (
        <div className="aspect-video w-full rounded-2xl overflow-hidden bg-black border border-[#273042]">
          <video src={streamUrl || `${process.env.REACT_APP_BACKEND_URL}${v.streamPath}`}
            controls playsInline controlsList="nodownload" onContextMenu={(e) => e.preventDefault()} className="w-full h-full" data-testid="vod-video" />
        </div>
      ) : v.iframe ? (
        <div className="aspect-video w-full rounded-2xl overflow-hidden bg-black border border-[#273042]">
          <iframe title={v.title} src={v.iframe} className="w-full h-full" allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;" allowFullScreen data-testid="vod-iframe" />
        </div>
      ) : (
        <div className="aspect-video w-full rounded-2xl bg-[#121620] border border-[#273042] flex items-center justify-center text-slate-400">This video is still processing — please check back shortly.</div>
      )}

      <h1 className="text-xl sm:text-2xl font-bold text-white mt-4">{v.title}</h1>
      <p className="text-sm text-slate-500 mt-1">{v.category} · <Eye className="w-4 h-4 inline" /> {v.views || 0} views</p>
      {v.description && <p className="text-slate-400 text-sm mt-3 whitespace-pre-line">{v.description}</p>}
    </div>
  );
}
